// Used when reporting module/story impression/click data for lineup

import type * as React from "react";
import { type REGIONS } from "@bloomberg/media-ui-nav/dist/types";
import type { Targeting } from "@foundation/sparkle/lib/AdTargeting/TargetingProvider";

import type { DROPDOWN_LABEL } from "~constants";
import type { AdConfig } from "~api/getAdConfig";

import type { CuratedItem, Image, Person, VariationComponent } from "./types";

export type SizeConfigs = {
    [key: string]: { imageSizes: string; imageWidth: number };
};

export type DropdownLabel = (typeof DROPDOWN_LABEL)[number];

export type MappedEditions = {
    premium: string;
    "premium-africa": string;
    "premium-asia": string;
    "premium-canada": string;
    "premium-europe": string;
    "premium-middle-east": string;
    "premium-uk": string;
};

export type SocialAccount =
    | "Facebook"
    | "Instagram"
    | "LinkedIn"
    | "Twitter"
    | "YouTube";

export interface ModuleTracking {
    id: string;
    title: string;
    abbaId?: string;
    abbaExperiments?: string;
}

export type AdParamsObject = {
    pageId: string;
    position: string;
    dimensions: {
        all: number[][];
        large_desktop: number[][];
        small_desktop: number[][];
        tablet: number[][];
        mobile: unknown[];
    };
    type: string;
    targeting: { position: string; page: string; edition: string };
    containerId: string;
};

export interface Audio {
    url: string;
    duration: number;
}

export interface Newsletter {
    title?: string;
    images?: Image[];
    defaultHeadline?: string;
    defaultEyebrow?: string;
}

export interface Dimensions {
    [key: string]: {
        aspectRatio: number;
        width: number;
    };
}

export interface PaginationProps {
    onRequestPaginate: () => void;
    paginationLoading: boolean;
    paginationAvailable: boolean;
}

export interface TrackingAttributes {
    ref?: React.MutableRefObject<null>;
    onClick?: React.MouseEventHandler;
    "data-tracking-id"?: string;
    "data-tracking-type"?: string;
    "data-module-id"?: string;
    "data-tracking-title"?: string;
    "data-parent-id"?: string;
    "data-story-count"?: number;
    "data-abba-id"?: string;
    "data-abba-experiments"?: string;
}

export type StoryBlockProps = Pick<VariationComponent, "tracking"> & {
    story: CuratedItem;
    storyCount?: number;
};

export interface Author {
    id: string;
    name: string;
    bio?: string;
    thumbnail?: Image;
    photoDisplayed: boolean;
    title: string;
    slug: string;
    twitter: string;
    items: CuratedItem[];
}

export interface AuthorPageProps {
    authorSlug?: string;
    authorType: string;
    author: Person;
    region?: REGIONS;
    countryCode?: string;
    adTargetings?: Targeting;
    adConfig: AdConfig;
}

export interface AuthorZonesProps {
    authorSlug?: string;
    authorType: string;
    author: Person;
}

export enum AUTHOR_TYPE {
    OPINION = "opinion",
}

export interface Record {
    id: string;
    title: string;
    url?: string;
}

export interface RecordStory {
    id: string;
    headline: string;
    url: string;
    slug: string;
}

export type RecordWithStories = Record & { stories: RecordStory[] };

export interface RelatedRecordStories {
    [storySlugKey: string]: RecordWithStories;
}

export interface RecordPageData {
    pageId: string;
    modules: RecordWithStories[];
}

export enum HEADLINE_SIZE {
    EXTRA_EXTRA_LARGE = "extraExtraLarge",
    EXTRA_LARGE = "extraLarge",
    LARGE = "large",
    MEDIUM = "medium",
    SMALL = "small",
    EXTRA_SMALL = "extraSmall",
    FULL_ZONE = "full",
    UNSTYLED = "unstyled",
}

export enum RequestStatus {
    NotStarted = "notStarted",
    Pending = "pending",
    Done = "done",
    Error = "error",
}
